import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSearchParams } from 'react-router-dom';

import { getContactSubmissions, updateContactSubmission } from '../../services/contactService';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
];

const ContactSubmissionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'all';

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState(
    STATUS_OPTIONS.some((option) => option.value === initialStatus) ? initialStatus : 'all',
  );

  const loadSubmissions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getContactSubmissions();
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Unable to load contact submissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    const params = {};
    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }
    setSearchParams(params, { replace: true });
  }, [statusFilter, setSearchParams]);

  const filteredSubmissions = useMemo(() => {
    if (statusFilter === 'all') {
      return submissions;
    }
    return submissions.filter((submission) => submission.status === statusFilter);
  }, [statusFilter, submissions]);

  const handleUpdate = async (submission, updates) => {
    try {
      const payload = { ...submission, ...updates };
      await updateContactSubmission(submission.id, payload);
      setSubmissions((prev) =>
        prev.map((item) => (item.id === submission.id ? { ...item, ...updates } : item)),
      );
    } catch (err) {
      setError('Unable to update submission.');
    }
  };

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Contact Submissions</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadSubmissions} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : submissions.length === 0 ? (
        <Typography color="text.secondary">No contact submissions yet.</Typography>
      ) : (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} mb={3}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All statuses</MenuItem>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="text"
              onClick={() => setStatusFilter('all')}
              sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
            >
              Reset filters
            </Button>
          </Stack>

          {filteredSubmissions.length === 0 ? (
            <Typography color="text.secondary">No submissions match the selected filters.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Submitted At</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSubmissions.map((submission) => (
                    <TableRow key={submission.id} hover>
                      <TableCell>{new Date(submission.submitted_at).toLocaleString()}</TableCell>
                      <TableCell>{submission.name}</TableCell>
                      <TableCell>{submission.email}</TableCell>
                      <TableCell>{submission.phone}</TableCell>
                      <TableCell>{submission.subject}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={submission.status}
                          onChange={(event) => handleUpdate(submission, { status: event.target.value })}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={submission.response_notes || ''}
                          size="small"
                          multiline
                          minRows={2}
                          onChange={(event) => handleUpdate(submission, { response_notes: event.target.value })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Container>
  );
};

export default ContactSubmissionsPage;
